import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Container,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { t } from 'i18next';
import { useDebounce } from 'use-debounce';
import { ApiSubtag } from '../../../api/hexa/ApiSubtag';
import SubtagDetail from './SubtagDetail';
import { Subtag } from '../../../api/hexa/models/SubtagModels';
import ModeEditIcon from '@mui/icons-material/ModeEdit';

const SubtagsList = () => {
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<Subtag>();
  const [rows, setRows] = useState<Subtag[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchFullText, setSearchFullText] = useState<string>('');
  const [debounceSearchFullText] = useDebounce(searchFullText, 500);
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  const columns = useMemo<GridColDef<Subtag>[]>(
    () => [
      { field: 'id', headerName: t('subtags_list.id'), flex: 1, maxWidth: 100 },
      { field: 'subtag', headerName: t('subtags_list.subtag'), flex: 1 },
      {
        field: 'description',
        headerName: t('subtags_list.description'),
        flex: 1,
      },
      {
        field: 'datasets',
        headerName: t('subtags_list.dataset'),
        flex: 1,
        renderCell: (params) => params.value?.join(', '),
      },
      {
        field: 'actions',
        type: 'actions',
        maxWidth: 50,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={
              <Tooltip title={t('subtags_list.edit')}>
                <ModeEditIcon color="primary" />
              </Tooltip>
            }
            onClick={() => {
              handleClickOpen(params.row);
            }}
            label={t('subtags_list.edit')}
          />,
        ],
      },
    ],
    []
  );

  /**
   * Fetch the paginated subtags from the API and set the rows state
   */
  const fetchSubtags = useCallback(async () => {
    setLoading(true);
    try {
      const { total, subtags } = await ApiSubtag.index({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        search: debounceSearchFullText,
      });
      setRowCount(total);
      setRows(subtags);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the subtag list: 
        ${error.response?.data.error ?? 'Error fetching the subtag list'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debounceSearchFullText]);

  useEffect(() => {
    fetchSubtags();
  }, [fetchSubtags]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const handleClickOpen = (row?: Subtag) => {
    row ? setRow(row) : setRow(undefined);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Container>
      <Card sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
        <Stack
          spacing={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Search"
              variant="outlined"
              fullWidth
              size="small"
              value={searchFullText}
              sx={{ backgroundColor: 'white' }}
              onChange={(e) => setSearchFullText(e.target.value)}
            />
          </Stack>
          <DataGridPremium
            getRowId={(row) => row.id}
            onRowClick={(params) => handleClickOpen(params.row)}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            paginationMode="server"
            pagination={true}
            pageSizeOptions={[10, 25, 50, 100]}
            autoHeight={true}
            rows={rows}
            columns={columns}
            density={'compact'}
          />
        </Stack>
        <Snackbar
          open={error.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error">
            {error.message}
          </Alert>
        </Snackbar>
      </Card>
      {open && (
        <SubtagDetail
          open={open}
          clickedRow={row}
          onClose={handleClose}
          subtagListUpdate={fetchSubtags}
        />
      )}
    </Container>
  );
};

export default SubtagsList;
